import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';

/**
 * PIN-style date input — 8 fixed digit places grouped as DD / MM / YYYY.
 * Type digits like a passcode: 1, 5, 0, 3, 2, 0, 2, 5 → 15/03/2025
 * Backspace clears right-to-left. Click to focus. No cursor visible.
 *
 * @param {string} value - ISO date string 'YYYY-MM-DD' or '' for empty
 * @param {function} onChange - Called with ISO date string when all 8 digits entered
 * @param {function} onComplete - Called after last digit committed (parent can move focus)
 * @param {boolean} readOnly - Display only, no interaction
 * @param {string} className - Optional wrapper class
 */
const DateInput = forwardRef(({ value = '', onChange, onComplete, onNavigate, readOnly = false, className = '', ariaLabel = 'Date input' }, ref) => {
  // Parse 'YYYY-MM-DD' → [D, D, M, M, Y, Y, Y, Y]
  const dateToDigits = useCallback((isoStr) => {
    if (!isoStr) return [0, 0, 0, 0, 0, 0, 0, 0];
    const [y, m, d] = isoStr.split('-');
    return [
      parseInt(d?.[0] || '0'), parseInt(d?.[1] || '0'),
      parseInt(m?.[0] || '0'), parseInt(m?.[1] || '0'),
      parseInt(y?.[0] || '0'), parseInt(y?.[1] || '0'),
      parseInt(y?.[2] || '0'), parseInt(y?.[3] || '0'),
    ];
  }, []);

  // [D, D, M, M, Y, Y, Y, Y] → 'YYYY-MM-DD'
  const digitsToDate = useCallback((d) => {
    const day = `${d[0]}${d[1]}`;
    const month = `${d[2]}${d[3]}`;
    const year = `${d[4]}${d[5]}${d[6]}${d[7]}`;
    return `${year}-${month}-${day}`;
  }, []);

  const isValidDate = useCallback((d) => {
    const day = d[0] * 10 + d[1];
    const month = d[2] * 10 + d[3];
    const year = d[4] * 1000 + d[5] * 100 + d[6] * 10 + d[7];
    if (month < 1 || month > 12 || day < 1 || year < 1900 || year > 2099) return false;
    const daysInMonth = new Date(year, month, 0).getDate();
    return day <= daysInMonth;
  }, []);

  const isEmpty = useCallback((d) => d.every(v => v === 0), []);

  const [digits, setDigits] = useState(() => dateToDigits(value));
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rejected, setRejected] = useState(false);
  const hiddenInputRef = useRef(null);
  const pendingPositionRef = useRef(0);
  const committedRef = useRef(false);

  useImperativeHandle(ref, () => ({
    focus: (position = 0) => {
      if (readOnly) return;
      pendingPositionRef.current = Math.min(7, Math.max(0, position));
      hiddenInputRef.current?.focus();
    },
  }));

  // Sync from parent when not editing
  useEffect(() => {
    if (activeIndex === -1) {
      setDigits(dateToDigits(value));
    }
  }, [value, activeIndex, dateToDigits]);

  const handleFocus = () => {
    if (readOnly) return;
    pendingPositionRef.current = 0;
    hiddenInputRef.current?.focus();
  };

  const handleBlur = () => {
    setActiveIndex(-1);
    if (committedRef.current) {
      committedRef.current = false;
      return;
    }
    if (!isEmpty(digits) && isValidDate(digits)) {
      const dateStr = digitsToDate(digits);
      if (dateStr !== value) onChange(dateStr);
    } else if (!isEmpty(digits)) {
      setDigits(dateToDigits(value));
    }
  };

  const handleKeyDown = (e) => {
    e.preventDefault();
    const key = e.key;

    if (key === 'Backspace') {
      const newDigits = [...digits];
      if (activeIndex > 0) {
        const prev = activeIndex - 1;
        newDigits[prev] = 0;
        setDigits(newDigits);
        setActiveIndex(prev);
      } else {
        newDigits[0] = 0;
        setDigits(newDigits);
      }
      return;
    }

    if (key === 'Tab') {
      hiddenInputRef.current?.blur();
      return;
    }

    if (key === 'Escape') {
      setDigits(dateToDigits(value));
      hiddenInputRef.current?.blur();
      return;
    }

    if (key === 'ArrowRight') {
      if (activeIndex < 7) {
        setActiveIndex(activeIndex + 1);
      } else if (onNavigate) {
        onNavigate('next', activeIndex);
      }
      return;
    }
    if (key === 'ArrowLeft') {
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      } else if (onNavigate) {
        onNavigate('prev', activeIndex);
      }
      return;
    }

    if (!/^[0-9]$/.test(key)) return;

    const digit = parseInt(key);
    const newDigits = [...digits];
    newDigits[activeIndex] = digit;

    // Inline validation per digit position
    // Day: DD (01-31)
    if (activeIndex === 0 && digit > 3) return;
    if (activeIndex === 1 && newDigits[0] === 3 && digit > 1) return;
    if (activeIndex === 1 && newDigits[0] === 0 && digit === 0) return; // day 00

    // Month: MM (01-12)
    if (activeIndex === 2 && digit > 1) return;
    if (activeIndex === 3 && newDigits[2] === 1 && digit > 2) return;
    if (activeIndex === 3 && newDigits[2] === 0 && digit === 0) return; // month 00

    // Year: first digit must be 1 or 2 (1900-2099)
    if (activeIndex === 4 && digit !== 1 && digit !== 2) return;

    setDigits(newDigits);

    if (activeIndex < 7) {
      setActiveIndex(activeIndex + 1);
    } else {
      // All 8 entered — validate and commit
      if (isValidDate(newDigits)) {
        const dateStr = digitsToDate(newDigits);
        onChange(dateStr);
        committedRef.current = true;
        hiddenInputRef.current?.blur();
        if (onComplete) onComplete();
      } else {
        setRejected(true);
        setTimeout(() => {
          setRejected(false);
          setDigits(dateToDigits(value));
          setActiveIndex(0);
        }, 600);
      }
    }
  };

  const isFocused = activeIndex >= 0;

  const digitBox = (i) => (
    <div
      key={i}
      className={`w-5 h-7 rounded flex items-center justify-center font-mono text-sm font-semibold transition-colors
        ${rejected
          ? 'bg-error/20 text-error'
          : isFocused && activeIndex === i
            ? 'bg-primary text-primary-content'
            : readOnly
              ? 'bg-base-200/50 text-base-content/50'
              : isEmpty(digits)
                ? 'bg-base-300/40 text-base-content/30'
                : 'bg-base-300/60 text-blue-600'
        }`}
    >
      {isEmpty(digits) && !isFocused
        ? ['D','D','M','M','Y','Y','Y','Y'][i]
        : digits[i]}
    </div>
  );

  const sep = (
    <span className={`font-bold mx-0.5 text-sm transition-colors ${
      rejected ? 'text-error/40' : readOnly ? 'text-base-content/25' : 'text-blue-600/40'
    }`}>/</span>
  );

  return (
    <div
      className={`inline-flex items-center gap-0.5 select-none ${readOnly ? 'cursor-default' : 'cursor-text'} ${className}`}
      onClick={handleFocus}
      role="textbox"
      aria-label={ariaLabel}
      tabIndex={-1}
    >
      {!readOnly && (
        <input
          ref={hiddenInputRef}
          className="sr-only"
          onFocus={() => { setActiveIndex(pendingPositionRef.current); pendingPositionRef.current = 0; }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          aria-hidden="true"
          readOnly
        />
      )}

      {/* DD */}
      {digitBox(0)}{digitBox(1)}
      {sep}
      {/* MM */}
      {digitBox(2)}{digitBox(3)}
      {sep}
      {/* YYYY */}
      {digitBox(4)}{digitBox(5)}{digitBox(6)}{digitBox(7)}
    </div>
  );
});

DateInput.displayName = 'DateInput';

export default DateInput;
